/**
 * Mock-mode copies of the two how-to guide articles the backend seeds from
 * `seed/gym_guides.json`. Same step ids, titles and instructions, including the
 * `PLACEHOLDER:` lines a manager is meant to fill in and the `{{code:...}}`
 * tokens the opening guide carries, so `/guides/:slug` renders offline exactly
 * as it will against production.
 */

export const GUIDE_FOLDER = { id: 'fld-opening-closing', name: 'Opening & Closing', parentId: null };

const step = (order, id, title, ...lines) => ({
    id,
    order,
    title,
    instruction: lines.join('\n'),
    required: true,
    evidence: { method: 'confirm' },
    media: [],
});

// The owner is recording one short video per step; until those land, one
// placeholder link keeps the embed on screen in mock mode.
const withVideo = (base, url, title) => ({ ...base, media: [{ kind: 'youtube', url, title }] });

const guideArticle = ({ id, title, body, steps }) => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return {
        id,
        title,
        folderId: GUIDE_FOLDER.id,
        status: 'published',
        whenNeeded: 'before-shift',
        requiredReading: false,
        visibility: [],
        version: 1,
        updatedAt: d.toISOString(),
        updatedBy: 'seed_gym_guides',
        createdBy: 'seed_gym_guides',
        seededAt: d.toISOString(),
        externalLinks: [],
        body: body.join('\n'),
        steps,
    };
};

export const seedGuideArticles = () => [
    guideArticle({
        id: 'guide-opening-the-gym',
        title: 'How to open the gym',
        body: [
            'Everything to do from the front door to the first member, in order. Work through it top to bottom; the task check-off in ACC comes at the end. Full SOP: `/knowledge-base?article=sop-gym-opening`.',
            '',
            '> PLACEHOLDER: add the opening walk-through video link here.',
        ],
        steps: [
            step(1, 'unlock-the-front-door', 'Get the key and unlock the front door',
                'The front door key is in the lockbox. Lockbox code: {{code:lockbox}}. Unlock the door and go straight to the back.'),
            step(2, 'disarm-the-alarm', 'Disarm the alarm at the ADT panel',
                'The alarm starts counting down the moment the door opens and WILL call the police if it is not disarmed in time, so this comes before anything else. The ADT panel is in the back where the supplies are. Enter your personal disarm code: {{code:alarm-disarm}}. Wait for the panel to confirm. If the panel does not confirm, contact management right away.'),
            step(3, 'unlock-the-door-bar', 'Unlock the door bar',
                'The door bar must be unlocked whenever the gym is open with people inside. It is a legal requirement, and members cannot get in otherwise. The key is a small allen-key style tool on the window sill to the right of the door, usually the higher sill. Insert it into the hole near the left end of the push bar and turn until the bar unlatches. Push the bar in while you turn; that locks the bar inward. When done the bar sits in, cannot be pushed out, and the door swings freely. Then put the lockbox key back in the lockbox so it is there for the next opener.'),
            step(4, 'lights-on', 'Lights on',
                'Facing the front door, the light switches are to the right of it. Turn each one on; they are preset. Walk to the back door and turn on the light for the drinking fountain area. Bathroom lights are automatic.'),
            withVideo(step(5, 'tvs-on', 'TVs on',
                'Go to each TV. On the bottom right edge of the TV there is a small button; press it once. The TV turns on and lands on the booth PC on its own: they are Samsung TVs with one input. If a TV comes up blank or on the wrong screen, press the button again to turn it off, wait a moment, and press once more.'),
            'https://www.youtube.com/watch?v=dQw4w9WgXcQ', 'TVs on (example)'),
            step(6, 'clock-in', 'Clock in on the iPad',
                'The iPad is in booth 14, on charge. Open the Auris app and clock in with your employee code. Keep the iPad with you for the next step.'),
            step(7, 'music-on', 'Music on at 45 to 50 percent',
                'On the same iPad, switch to the Sonos app and pick any of the music there that fits the floor. Set the volume between 45 and 50 percent. Nothing with profanity or aggressive lyrics during mixed-age hours.'),
            step(8, 'open-sign', 'OPEN sign on',
                'Turn on the OPEN sign.'),
            step(9, 'check-slack-and-schedule', 'Check Slack and the schedule',
                'Read Slack for anything since the last shift. Check the first session times on the schedule.'),
            step(10, 'headsets-on', 'Start up each booth',
                'Do this for every booth, one at a time.',
                '',
                '1. Take a charged battery from the charger below the hero trainer main PC and clip it onto that booth\'s BOBOVR headstrap.',
                '2. Turn on the Quest 3 headset.',
                '3. In the headset, open Virtual Desktop and connect to that booth\'s PC.',
                '4. From the main PC, Splashtop into the booth PC and check that Booth Minder, SteamVR and Space Calibrator are all open, with SteamVR connected.',
                '5. Look through the headset: the space room with the grid around you means the booth is connected.',
                '',
                'If a booth will not connect or the grid does not appear: `/knowledge-base?article=sop-advanced-alignment-troubleshooting` and `/knowledge-base?article=sop-launching-a-game-with-booth-minder`. ACC reads a headset as on when its Virtual Desktop session is active, so this step is also what the booth check on the Hero Trainer Center sees.'),
            step(11, 'booth-readiness', 'Booth readiness check',
                'Walk every booth once more: lights, cables, resistance and calibration. Base stations and machine pucks show green. Wipe and prep each booth for the first wave. Booth prep SOP: `/knowledge-base?article=sop-booth-prep-for-member-workout`.'),
            step(12, 'facility-inspection', 'Front-of-house check',
                'A check, not a restock: bathrooms clean and stocked, lobby seating wiped, Power-Up Station and protein filled, front desk reset, clean arm garments folded from the laundry and in the drawers. Anything short goes in the Daily Report for the closer.'),
            step(13, 'ready-for-members', 'Ready for members',
                'Greet every member on entry: stand, smile, eye contact, use their name. Now check off the opening tasks in ACC so the day is on record: /shift-flow/open.'),
        ],
    }),
    guideArticle({
        id: 'guide-closing-the-gym',
        title: 'How to close the gym',
        body: [
            'Everything to do after the last member leaves, in order. Booth PCs stay on; only headsets power off. The task check-off in ACC comes at the end. Full SOP: `/knowledge-base?article=sop-gym-closing`.',
            '',
            '> PLACEHOLDER: add the closing walk-through video link here.',
        ],
        steps: [
            step(1, 'reset-every-booth', 'Reset every booth',
                'Wipe the equipment, remove trash, and prep each booth for tomorrow. Booth shutdown SOP: /knowledge-base?article=sop-booth-shutdown.'),
            step(2, 'power-down-headsets', 'Shut down each booth',
                'For every booth: turn the headset off, take the battery off the BOBOVR headstrap and put it on the charger below the hero trainer main PC. Booth shutdown SOP: `/knowledge-base?article=sop-booth-shutdown`.'),
            step(3, 'charge-headsets', 'Everything on charge',
                'In each booth confirm three things are plugged in and charging: the headset, the unicorn horn puck, and both hand tracking pucks. Booth PCs stay on.'),
            step(4, 'plug-in-the-ipad', 'Plug in the booth 14 iPad',
                'Plug the iPad in booth 14 back in so it charges overnight.'),
            step(5, 'trash-and-bathrooms', 'Trash and bathrooms',
                'Empty every trash can. Clean and restock the bathrooms. Bathroom SOP: /knowledge-base?article=sop-cleaning-bathrooms.'),
            step(6, 'restock', 'Restock',
                'Refill the arm garment drawer from the laundry. Restock the Unity Gem Reward Zone and the Power-Up Station. Restocking is a closing job; opening only checks.'),
            step(7, 'floors-and-surfaces', 'Floors and surfaces',
                'Sweep or vacuum the floors. Wipe countertops and lobby furniture. Wet-mop on Tuesdays and Thursdays. SOPs: /knowledge-base?article=sop-vacuuming, /knowledge-base?article=sop-mopping, /knowledge-base?article=sop-cleaning-furniture.'),
            step(8, 'screens-and-music-off', 'Screens and music off',
                'Turn off the TVs and the music. Leave the computers on. Turn off the OPEN sign.'),
            step(9, 'daily-report', 'Daily report',
                'Log the Daily Report in ACC: booth issues, restock needs, member highlights. It posts the recap to the Meridian channel for you. `/hero-trainer-daily-report`'),
            step(10, 'clock-out-and-lights', 'Clock out and lights off',
                'Clock out. Turn off the interior lights.'),
            step(11, 'arm-and-lock', 'Arm the alarm and lock up',
                'Confirm everyone has left. At the ADT panel in the back enter your code and select Arm Away: you have 60 seconds to get out. If the panel does not confirm, contact management. Lock the front door with the hex key, then pull the door to confirm it locked. Now check off the closing tasks in ACC: `/shift-flow/close`.'),
        ],
    }),
];
